import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  VStack,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DbTable } from '@finley-technologies/finley-types';

import { Alert } from '../../common-frontend/components/Alert';
import { DataTapeErrorView } from '../../manualDataTapeUploads/ui/DataTapeErrorView';
import {
  CsvTapeTable,
  XlsxTapeTable,
} from '../../manualDataTapeUploads/ui/DataTapeFormatModal';
import { useGraph, useNode } from '../hooks/bbplMonitorContext';
import { useStatus } from '../hooks/bbplStatusContext';
import { DownloadMenuButton } from './DownloadMenuButton';
import { getDrawerDetails } from './utils';

// TODO(jon): should probably apply this globally.
const tabProps = {
  marginTop: 0,
  marginRight: 0,
  fontSize: 'sm',
  _selected: {
    color: 'blue.400',
    borderBottomColor: 'blue.400',
    borderBottomWidth: '4px',
    boxShadow: 'none',
  },
};

const NodeStatusAlert = ({
  nodeStatus,
}: {
  nodeStatus: DbTable.ArtifactInstance.Status | undefined;
}) => {
  switch (nodeStatus) {
    case 'error':
      // We have dedicated error handling UI.
      return <></>;
    case undefined:
      return (
        <Alert status="info">
          <Text>Not started</Text>
        </Alert>
      );
    case 'initializing':
    case 'inProgress':
      return (
        <Alert status="info">
          <Text>In progress</Text>
        </Alert>
      );
    case 'success':
      return (
        <Alert status="success">
          <Text>Successfully completed</Text>
        </Alert>
      );
    default:
      return finleyAssertUnreachable(nodeStatus);
  }
};

type DrawerTabType = 'errors' | 'dataDictionary';

type BBPLMonitorDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
};

export const BBPLMonitorDrawer = ({
  nodeId,
  isOpen,
  onClose,
}: BBPLMonitorDrawerProps) => {
  const node = useNode(nodeId);
  const { status } = useStatus();
  const { state } = useGraph();

  const nodeStatus = node?.instance?.status;
  const tapeDetails = useMemo(() => getDrawerDetails(state, nodeId), [
    nodeId,
    state,
  ]);

  const availableTabs = useMemo<DrawerTabType[]>(() => {
    const tabs: DrawerTabType[] = [];
    if (tapeDetails.errors.length > 0) {
      tabs.push('errors');
    }
    // TODO(jon): also show data dictionary for type === 'alreadyUploaded'.
    if (node?.uploadDetails.type === 'file') {
      tabs.push('dataDictionary');
    }
    return tabs;
  }, [tapeDetails.errors.length, node?.uploadDetails.type]);

  const [selectedTab, setSelectedTab] = useState<DrawerTabType | null>(null);
  useEffect(() => {
    setSelectedTab((prevSelectedTab) => {
      if (prevSelectedTab && availableTabs.includes(prevSelectedTab)) {
        return prevSelectedTab;
      }
      if (availableTabs.length > 0) {
        return availableTabs[0];
      }
      return null;
    });
  }, [availableTabs]);

  if (!node) {
    return null;
  }

  const artifactInstanceId = node.instance?.id || null;
  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="xl" closeOnOverlayClick>
      <DrawerOverlay />
      <DrawerContent padding="40px">
        <HStack justifyContent="space-between" alignItems="start" w="100%">
          <DrawerHeader flexGrow={1} maxWidth="90%">
            {node.artifact.externalDisplayName}
          </DrawerHeader>
          <Box p="16px">
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              onClick={onClose}
              size="sm"
              variant="ghost"
            />
          </Box>
        </HStack>
        <DrawerBody>
          <VStack spacing="20px" alignItems="flex-start">
            {artifactInstanceId && (
              <DownloadMenuButton
                buttonLabel="Download"
                artifactInstanceId={artifactInstanceId}
                creditFacilityId={status.creditFacilityId}
              />
            )}
            {/* NodeStatusAlert is a placeholder in cases where there are no tabs. */}
            {availableTabs.length === 0 && (
              <NodeStatusAlert nodeStatus={nodeStatus} />
            )}
            {selectedTab && availableTabs.includes(selectedTab) && (
              <Tabs width="100%" index={availableTabs.indexOf(selectedTab)}>
                <TabList>
                  {availableTabs.map((tab) => (
                    <Tab
                      {...tabProps}
                      key={tab}
                      onClick={() => setSelectedTab(tab)}
                    >
                      {commonUtils.toTitleCase(tab)}
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
            )}
            {/* TODO(jon): this sidebar isn't wide enough for errors */}
            {selectedTab === 'errors' && (
              <DataTapeErrorView
                errors={tapeDetails.errors}
                numOfRows={100}
                collapsible={false}
              />
            )}
            {/* TODO(jon): this sidebar isn't wide enough for data dictionary */}
            {selectedTab === 'dataDictionary' && (
              <>
                {tapeDetails.csvTape && (
                  <CsvTapeTable csvTape={tapeDetails.csvTape} />
                )}
                {tapeDetails.xlsxTape && (
                  <XlsxTapeTable xlsxTape={tapeDetails.xlsxTape} />
                )}
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const BBPLMonitorOptionalDrawer = () => {
  const { selectedNodeId, setSelectedNode } = useGraph();
  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = () => {
    setIsOpen(false);
    setSelectedNode(null);
  };

  React.useEffect(() => {
    setIsOpen(!!selectedNodeId);
  }, [selectedNodeId]);

  if (!selectedNodeId) {
    return null;
  }
  return (
    <BBPLMonitorDrawer
      nodeId={selectedNodeId}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};
